import { useState } from 'react'
import { Segment, Form, Image, Icon, Header, List, Label, Button, Modal, ModalContent } from 'semantic-ui-react'
import { attachmentSectionDefinitions, pendingAttachmentFile, shouldShowAttachmentSection } from './assetProblemAttachmentConfig'
import { BlobVideoPlayerModal } from '../common'

function renderAttachmentRow (content, actions, middleAligned = false) {
    const listClassName = middleAligned ? 'ui middle aligned horizontal list' : 'ui horizontal list'
    const itemClassName = middleAligned ? 'middle aligned item' : 'item'

    return (
        <div className={listClassName}>
            <div className={itemClassName}>
                {content}
            </div>
            <div className={itemClassName}>
                {actions}
            </div>
        </div>
    )
}

function renderActionButtons (buttons) {
    if (buttons.length === 1) {
        return buttons[0]
    }

    return <div className="ui vertical mini basic buttons">{buttons}</div>
}

function renderPreview (attachmentType, pendingItem, onDelete) {
    const file = pendingAttachmentFile(pendingItem)
    const previewUrl = pendingItem?.previewUrl

    const content = attachmentType === 'picture' && previewUrl
        ? <Image src={previewUrl} size="small" bordered alt={file.name} />
        : attachmentType === 'video' && previewUrl
            ? (
                <video controls style={{ maxWidth: '180px' }}>
                    <source src={previewUrl} />
                </video>
            )
            : <Label basic>{file.name}</Label>

    const actions = renderActionButtons([
        <Button
            key="delete"
            basic
            size="mini"
            icon="trash"
            color="red"
            content="Delete"
            onClick={onDelete}
        />
    ])

    return renderAttachmentRow(content, actions, true)
}

function renderExistingNonVideo (attachment, onOpenImage, onDelete) {
    const content = attachment.attachmentType === 'picture'
        ? (
            <Image
                src={attachment.fileUrl}
                size="small"
                bordered
                style={{ cursor: 'pointer' }}
                onClick={onOpenImage}
            />
        )
        : (
            <a href={attachment.fileUrl} target="_blank" rel="noreferrer">
                {attachment.originalFilename}
            </a>
        )

    const actions = renderActionButtons([
        <Button key="delete" basic size="mini" icon="trash" color="red" content="Delete" onClick={onDelete} />
    ])

    return renderAttachmentRow(content, actions)
}

function renderExistingVideo (attachment, onPlay, onDelete) {
    const content = attachment.thumbnailUrl
        ? (
            <Image
                src={attachment.thumbnailUrl}
                size="small"
                bordered
                style={{ cursor: 'pointer' }}
                onClick={onPlay}
            />
        )
        : <Label basic><Icon name="film" /> {attachment.originalFilename}</Label>

    const actions = renderActionButtons([
        <Button key="play" basic size="mini" icon="play" content="Play" onClick={onPlay} />,
        <Button key="delete" basic size="mini" icon="trash" color="red" content="Delete" onClick={onDelete} />
    ])

    return renderAttachmentRow(content, actions, true)
}

export const AssetProblemAttachments = ({
    pendingAttachments,
    existingAttachments,
    attachmentErrors,
    onAddFiles,
    onRemovePending,
    onDeleteExisting
}) => {
    const [playingVideo, setPlayingVideo] = useState(null)
    const [selectedImage, setSelectedImage] = useState(null)

    return (
        <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <strong>Supporting files</strong>
            </div>
            {attachmentSectionDefinitions.filter(section => (
                shouldShowAttachmentSection(section, existingAttachments)
            )).map(section => {
                const pending = pendingAttachments[section.key] || []
                const existing = (existingAttachments || []).filter(
                    attachment => attachment.attachmentType === section.key
                )
                const showUploadField = section.uploadEnabled

                return (
                    <Segment key={section.key}>
                        {showUploadField && (
                            <Form.Field>
                                <label>{section.label}</label>
                                <p style={{ color: 'gray', fontSize: '0.9em' }}>{section.helper}</p>
                                <Form.Input
                                    type="file"
                                    multiple
                                    accept={section.accept}
                                    onChange={(e) => {
                                        const files = e.target.files
                                        onAddFiles(section.key, files)
                                        // Reset input after files are processed
                                        setTimeout(() => { e.target.value = '' }, 0)
                                    }}
                                    error={attachmentErrors[section.key]}
                                />
                            </Form.Field>
                        )}
                        {pending.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                {pending.map((pendingItem, index) => (
                                    <div key={`${section.key}-pending-${index}`}>
                                        {renderPreview(section.key, pendingItem, () => onRemovePending(section.key, index))}
                                    </div>
                                ))}
                            </div>
                        )}
                        {existing.length > 0 && (
                            <>
                                <Header as="h5">
                                    Saved {attachmentSectionDefinitions.find(def => def.key === section.key)?.label.toLowerCase()}
                                </Header>
                                <div>
                                    {existing.map(attachment => (
                                        <div key={attachment.id}>
                                            {attachment.attachmentType === 'video'
                                                ? renderExistingVideo(
                                                    attachment,
                                                    () => setPlayingVideo({
                                                        blobName: attachment.blobName,
                                                        title: attachment.originalFilename
                                                    }),
                                                    () => onDeleteExisting(attachment.id)
                                                )
                                                : renderExistingNonVideo(attachment, () => {
                                                    if (attachment.attachmentType === 'picture') {
                                                        setSelectedImage(attachment)
                                                    }
                                                }, () => onDeleteExisting(attachment.id))}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </Segment>
                )
            })}
            {playingVideo && (
                <BlobVideoPlayerModal
                    blobName={playingVideo.blobName}
                    title={playingVideo.title}
                    onClose={() => setPlayingVideo(null)}
                />
            )}
            <Modal open={!!selectedImage} onClose={() => setSelectedImage(null)} closeIcon size="large">
                <ModalContent style={{ textAlign: 'center', padding: '1rem' }}>
                    {selectedImage && (
                        <>
                            <img
                                src={selectedImage.fileUrl}
                                alt={selectedImage.originalFilename || 'Asset problem image'}
                                style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '4px' }}
                            />
                            <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#666' }}>
                                {selectedImage.originalFilename}
                            </div>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    )
}
